import { usePageTitle } from "@/hooks/use-page-title";
import { ComplianceScorecard } from "@/components/compliance-scorecard";
import { AnalyticsChart } from "@/components/analytics-chart";
import { MetricsCard } from "@/components/metrics-card";
import { TrendingUp, CheckCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from "react";
import { PageAbout } from "@/components/page-about";

interface ComplianceMetric {
  name: string;
  score: number;
  status: "excellent" | "good" | "warning" | "critical";
  details: string;
}

interface AnalyticsData {
  complianceMetrics: ComplianceMetric[];
  overallScore: number;
  fundingTrendData: Array<{ date: string; deposits: number; withdrawals: number }>;
  contributionData: Array<{ date: string; contributions: number; payments: number }>;
  activityTrendData: Array<{ date: string; walletEvents: number; auditEvents: number; securityEvents: number }>;
  summaryMetrics: {
    totalFunding: number;
    activeWallets: number;
    utilizationRate: number;
    auditCoverage: number;
  };
}

export default function Analytics() {
  usePageTitle("Analytics & Insights", "Platform analytics, compliance scorecards, and performance metrics for HealthWallet.");
  const [timeRange, setTimeRange] = useState("7d");

  const { data: analytics, isLoading } = useQuery<AnalyticsData>({
    queryKey: ["/api/analytics", timeRange],
    queryFn: async () => {
      const res = await fetch(`/api/analytics?timeRange=${timeRange}`, {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error("Failed to fetch analytics");
      }
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-semibold">Compliance Analytics</h1>
            <p className="text-muted-foreground mt-1">Performance metrics and trends</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 w-full" data-testid={`skeleton-metric-${i}`} />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-64 w-full" data-testid="skeleton-chart-1" />
            <Skeleton className="h-64 w-full" data-testid="skeleton-chart-2" />
          </div>
          <Skeleton className="h-96 w-full" data-testid="skeleton-scorecard" />
        </div>
      </div>
    );
  }

  const summaryMetrics = analytics?.summaryMetrics || {
    totalFunding: 0,
    activeWallets: 0,
    utilizationRate: 0,
    auditCoverage: 0,
  };

  const complianceMetrics = analytics?.complianceMetrics || [];
  const overallScore = analytics?.overallScore || 0;
  const fundingTrendData = analytics?.fundingTrendData || [];
  const contributionData = analytics?.contributionData || [];
  const activityTrendData = analytics?.activityTrendData || [];

  return (
    <div className="space-y-6">
      <PageAbout
        title="Compliance Analytics"
        description="Track healthcare funding performance with detailed wallet metrics and compliance trends. Monitor fund utilization, contribution patterns, and security compliance across all operations."
        features={[
          "Real-time compliance scorecard with category breakdown",
          "Wallet funding trend analysis showing deposits and withdrawals",
          "Contribution activity tracking for community fund performance",
          "System activity monitoring for proactive security oversight",
          "Customizable time ranges from 7 days to yearly analysis"
        ]}
        tips={[
          "Check the compliance scorecard daily for quick health check",
          "Use the 30-day view to identify contribution patterns",
          "Focus on categories below 90% for improvement priorities"
        ]}
      />
      
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Compliance Analytics</h1>
          <p className="text-muted-foreground mt-1">Performance metrics and trends</p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-48" data-testid="select-time-range">
            <SelectValue placeholder="Time range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
            <SelectItem value="1y">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricsCard
          title="Total Funding"
          value={`$${summaryMetrics.totalFunding.toLocaleString()}`}
          subtitle="Total contributions"
          icon={TrendingUp}
          data-testid="metric-total-funding"
        />
        <MetricsCard
          title="Active Wallets"
          value={summaryMetrics.activeWallets}
          subtitle="Currently active"
          icon={CheckCircle}
          data-testid="metric-active-wallets"
        />
        <MetricsCard
          title="Utilization Rate"
          value={`${summaryMetrics.utilizationRate}%`}
          subtitle="Fund utilization"
          icon={TrendingUp}
          data-testid="metric-utilization-rate"
        />
        <MetricsCard
          title="Audit Coverage"
          value={`${summaryMetrics.auditCoverage}%`}
          subtitle="Complete records"
          icon={CheckCircle}
          data-testid="metric-audit-coverage"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <AnalyticsChart
            title="Wallet Funding Trend"
            data={fundingTrendData}
            lines={[
              { dataKey: "deposits", name: "Deposits ($)", color: "hsl(var(--chart-1))" },
              { dataKey: "withdrawals", name: "Withdrawals ($)", color: "hsl(var(--chart-2))" },
            ]}
            yAxisLabel="$"
          />

          <AnalyticsChart
            title="Contribution Activity"
            data={contributionData}
            lines={[
              { dataKey: "contributions", name: "Contributions", color: "hsl(var(--chart-1))" },
              { dataKey: "payments", name: "Payments", color: "hsl(var(--chart-2))" },
            ]}
            yAxisLabel="Count"
          />

          <AnalyticsChart
            title="System Activity"
            data={activityTrendData}
            lines={[
              { dataKey: "walletEvents", name: "Wallet Events", color: "hsl(var(--chart-1))" },
              { dataKey: "auditEvents", name: "Audit Events", color: "hsl(var(--chart-3))" },
              { dataKey: "securityEvents", name: "Security Events", color: "hsl(var(--chart-4))" },
            ]}
            yAxisLabel="Count"
          />
        </div>

        <div>
          <ComplianceScorecard metrics={complianceMetrics} overallScore={overallScore} />
        </div>
      </div>
    </div>
  );
}
